import { useState } from "react";
import { format } from "date-fns";
import type { DateRange, Matcher } from "react-day-picker";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetTitle,
} from "@/components/ui/bottom-sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

// One date input for the whole app: a full-width bottom sheet on phones (the
// old 276px popover under a 146px trigger collision-flipped away from it) and
// a popover on desktop. `range` mode replaces the two unlinked single pickers
// that used to stand in for move-in/move-out.

type SingleProps = {
  mode: "single";
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
};
type RangeProps = {
  mode: "range";
  value: DateRange | undefined;
  onChange: (value: DateRange | undefined) => void;
};

type DateFieldProps = (SingleProps | RangeProps) & {
  placeholder?: string;
  /** Sheet/popover heading. */
  title?: string;
  disabled?: Matcher | Matcher[];
  fromMonth?: Date;
  defaultMonth?: Date;
  /** Show a clear (×) affordance when a value is set. */
  clearable?: boolean;
  className?: string;
  /** Extra content rendered above the calendar inside the sheet/popover. */
  hint?: React.ReactNode;
  /** Day states drawn on the calendar, e.g. blocked days in red. */
  modifiers?: Record<string, Matcher | Matcher[]>;
  modifiersClassNames?: Record<string, string>;
};

const fmt = (d: Date) => format(d, "MMM d, yyyy");

const labelFor = (props: DateFieldProps) => {
  if (props.mode === "single") return props.value ? fmt(props.value) : null;
  const { from, to } = props.value ?? {};
  if (from && to) return `${format(from, "MMM d")} → ${fmt(to)}`;
  if (from) return `${fmt(from)} → …`;
  return null;
};

export const DateField = (props: DateFieldProps) => {
  const { placeholder = "Pick a date", title, disabled, fromMonth, defaultMonth, clearable = true, className, hint, modifiers, modifiersClassNames } = props;
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const label = labelFor(props);

  const clear = () => (props.mode === "single" ? props.onChange(undefined) : props.onChange(undefined));

  const calendar =
    props.mode === "single" ? (
      <Calendar
        mode="single"
        selected={props.value}
        onSelect={(d) => {
          props.onChange(d);
          if (d) setOpen(false);
        }}
        disabled={disabled}
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        fromMonth={fromMonth}
        defaultMonth={defaultMonth ?? props.value}
        numberOfMonths={isMobile ? 2 : 1}
        initialFocus={!isMobile}
      />
    ) : (
      <Calendar
        mode="range"
        selected={props.value}
        onSelect={(r) => {
          props.onChange(r);
          if (r?.from && r?.to && !isMobile) setOpen(false);
        }}
        disabled={disabled}
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        fromMonth={fromMonth}
        defaultMonth={defaultMonth ?? props.value?.from}
        numberOfMonths={2}
        initialFocus={!isMobile}
      />
    );

  const trigger = (
    <div className={cn("relative", className)}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        aria-label={title ? (label ? `${title}: ${label}` : title) : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          "w-full justify-start text-left font-normal rounded-lg h-10 text-[13px] pr-9",
          label ? "text-foreground" : "text-muted-foreground",
        )}
      >
        <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label ?? placeholder}</span>
      </Button>
      {clearable && label && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            clear();
          }}
          aria-label="Clear dates"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 min-h-0 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <BottomSheet open={open} onOpenChange={setOpen}>
          <BottomSheetContent>
            <BottomSheetHeader>
              <BottomSheetTitle>{title ?? placeholder}</BottomSheetTitle>
            </BottomSheetHeader>
            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {hint && <div className="px-2 pb-2 text-xs text-muted-foreground">{hint}</div>}
              {calendar}
            </div>
            <BottomSheetFooter className="flex-row justify-between gap-2">
              <Button type="button" variant="ghost" className="rounded-full" onClick={clear} disabled={!label}>
                Clear
              </Button>
              <Button type="button" className="rounded-full px-6" onClick={() => setOpen(false)}>
                Done
              </Button>
            </BottomSheetFooter>
          </BottomSheetContent>
        </BottomSheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {hint && <div className="px-3 pt-3 text-xs text-muted-foreground max-w-[22rem]">{hint}</div>}
        {calendar}
      </PopoverContent>
    </Popover>
  );
};

export default DateField;
